import { Box, Link, Text } from '@nimbus-ds/components';
import { BILLING_NEW_FLOW_FREQUENCY } from 'App/featuresFlags';
import { useHasTags } from 'App/hooks';
import { SubmitButton } from 'commons/components';
import { useToastStatus } from 'commons/hooks';
import { dateFormat, Format } from 'commons/utils/date';
import CurrencyPriceWithFrequency from 'domains/Billing/Checkout/components/CurrencyPriceWithFrequency';
import {
  useExpirationDate,
  usePaySingleCharge,
} from 'domains/Billing/Checkout/hooks';
import { useGoToConceptDetails } from 'domains/Billing/Checkout/hooks/useGoToConceptDetails';
import { isPayableCharge } from 'domains/Billing/Checkout/hooks/utils';
import useTranslationBilling from 'domains/Billing/useTranslationBilling';
import RecurrentPaymentDataSkeleton from './Skeleton';
import {
  PaymentStatusTag,
  ChargePeriodDetail,
  SelectPlanRecurrency,
} from '../../../';
import { RecurrentPaymentChargeDisplayData } from '../../../../hooks';

interface RecurrentPaymentDataProps {
  data: RecurrentPaymentChargeDisplayData;
}

function RecurrentPaymentData({
  data: {
    amountCurrency,
    amountValue,
    description,
    nextExecutionDate,
    status,
    externalReference,
    concept,
    id,
    isProportional,
    fromDate,
    toDate,
    recurringFrequency,
    recurringInterval,
    scheduledDowngradeDate,
  },
}: RecurrentPaymentDataProps) {
  const t = useTranslationBilling(
    'checkout.plansAndSubscriptions.recurrentPaymentsStatus',
  );
  const { expiresIn } = useExpirationDate(concept, externalReference);
  const [hasRecurrencyTag] = useHasTags(BILLING_NEW_FLOW_FREQUENCY);
  const { onPay, setupStatus, executionStatus } = usePaySingleCharge(
    concept,
    externalReference,
  );

  const goToDetails = useGoToConceptDetails(
    'recurrentPaymentStatus',
    concept,
    externalReference,
    id,
  );

  useToastStatus({
    status: executionStatus.status,
    error: t('payButton.error'),
    progress: t('payButton.loading'),
  });

  if (!amountValue) return null;

  const periodData = {
    isProportional,
    fromDate,
    toDate,
  };
  const isPaid = status === 'PAID';
  const shouldAllowUpdatePlanRecurrency =
    concept === 'plan-cost' && status !== 'IN_PROCESS';

  return (
    <Box
      display="flex"
      flexDirection="row"
      paddingX="4"
      paddingY="none"
      justifyContent="space-between"
    >
      <Box display="flex" flexDirection="column" flex="1 1 60%" gap="1-5">
        <Box display="flex" flexDirection="column">
          <Text>{description}</Text>
          {!isPaid && (
            <>
              <ChargePeriodDetail {...periodData} />
              <PaymentStatusTag paymentStatus={status} />
            </>
          )}
          {isPaid &&
            (scheduledDowngradeDate ? (
              <Text fontSize="caption" color="neutral-textLow">
                {t('scheduledDowngrade', {
                  scheduledDowngradeDate: dateFormat(
                    scheduledDowngradeDate,
                    Format.DD_MM_YYYY,
                  ),
                })}
              </Text>
            ) : (
              <Text fontSize="caption" color="neutral-textLow">
                {t('nextExecution', {
                  nextExecutionDate: dateFormat(
                    nextExecutionDate,
                    Format.DD_MM_YYYY,
                    'UTC',
                  ),
                })}
              </Text>
            ))}
        </Box>
        <Link textDecoration="none" appearance="primary" onClick={goToDetails}>
          {t('details')}
        </Link>
      </Box>
      <Box
        display="flex"
        flexDirection="column"
        alignItems="flex-end"
        justifyContent="space-between"
        gap="2"
      >
        <Box
          display="flex"
          flexDirection="column"
          flex="1 1 40%"
          alignItems="flex-end"
          gap="2"
        >
          <CurrencyPriceWithFrequency
            amountValue={amountValue}
            amountCurrency={amountCurrency}
            recurringInterval={recurringInterval}
            recurringFrequency={recurringFrequency}
          />
          {!!expiresIn &&
            expiresIn > 0 &&
            status &&
            isPayableCharge(status) && (
              <Text>{t('expiresIn', { expiresIn })}</Text>
            )}
        </Box>
        {shouldAllowUpdatePlanRecurrency && <SelectPlanRecurrency />}
        {status &&
          hasRecurrencyTag &&
          isPayableCharge(status) &&
          setupStatus.isSuccess && (
            <SubmitButton isLoading={executionStatus.isLoading} onClick={onPay}>
              {t('payButton.text')}
            </SubmitButton>
          )}
      </Box>
    </Box>
  );
}

RecurrentPaymentData.Skeleton = RecurrentPaymentDataSkeleton;

export default RecurrentPaymentData;
